import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n } from '~Hooks';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Box, Button, BlueButton } from '~Styles';
import CashCounter from '~Components/CashCounter';
import NumberInput from '~ComponentsImproved/Numpad';
import { useCashUniverse } from '~Components/CashUniverse';

const ManuallyButton = styled(Button)`
  width: 168px;
`;

const CashCounterButton = styled(BlueButton)`
  width: 168px;
`;

interface Props {
  handleCashOut: (amount: number) => void;
  isHandy?: boolean;
  isShowing: boolean;
  onClose: () => void;
}

/**
 * Component for entering cash balance at end of day.
 * Provides two methods: Manual entry or Cash Counter.
 */
const EnterCashBalance: FC<Props> = ({ handleCashOut, isHandy, isShowing, onClose }) => {
  const { i18n } = useI18n();
  const cashUniverse = useCashUniverse();

  const [EODType, setEODType] = useState<'CASH_COUNTER' | 'MANUAL' | null>(null);

  const totalCashInRegister = ((cashUniverse?.totalCashRegister || 0) / 100).toString();

  const handleClose = () => {
    setEODType(null);
    onClose();
  };

  useEffect(() => {
    if (isHandy && isShowing) {
      setEODType('MANUAL');
    }
  }, [isHandy, isShowing]);

  const manuallyButton = useButton({
    onClick: () => {
      setEODType('MANUAL');
      onClose();
    },
  });

  const cashCounterButton = useButton({
    onClick: () => {
      setEODType('CASH_COUNTER');
    },
  });

  return (
    <>
      <Modal isShowing={isShowing && !isHandy} hide={handleClose}>
        <ModalWrapper>
          {EODType === null && (
            <>
              <Heading level={1} color="var(--colorWhite)">
                {i18n('EOD.EndOfDay.CashEntryType')}
              </Heading>

              <Box display="flex" gap="large">
                <ManuallyButton
                  fluid
                  size="large"
                  display="flex"
                  gap="xs"
                  alignItems="center"
                  {...manuallyButton}>
                  {i18n('CashCounter.Manually')}
                </ManuallyButton>
                <CashCounterButton
                  fluid
                  size="large"
                  display="flex"
                  gap="xs"
                  alignItems="center"
                  {...cashCounterButton}>
                  {i18n('CashCounter.Title')}
                </CashCounterButton>
              </Box>
            </>
          )}

          {EODType === 'CASH_COUNTER' && (
            <CashCounter
              onSubmit={(amountInCents) => {
                handleCashOut(amountInCents / 100);
                onClose();
              }}
            />
          )}
        </ModalWrapper>
      </Modal>

      {EODType === 'MANUAL' && (
        <NumberInput
          decimal
          initialValue={totalCashInRegister}
          onClose={() => {
            setEODType(null);
            onClose();
          }}
          onSubmit={(amount) => {
            handleCashOut(Number(amount));
            setEODType(null);
          }}
          small={isHandy ? true : false}
        />
      )}
    </>
  );
};

export default EnterCashBalance;
